import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

/**
 * Category → Submenu → Job pickers for Create Ticket.
 *
 * Inside a <form>, Radix Select keeps a hidden native <select> in sync with
 * the controlled value. When a value is committed before its <SelectItem> has
 * registered its native <option> (always the case when a search result sets
 * category + submenu + job together, even in one render), the native select
 * falls back to '' and Radix reports onValueChange(''). No item here has an
 * empty value, so '' can never be a user's choice: it is ignored, and the
 * selection the parent committed stands. Clearing is done by the parent
 * directly (add job, cancel, category reset), never through these callbacks.
 */
export interface CatalogJobOption {
  id: string;
  label: string;
  minutes_default: number;
}

interface CatalogJobSelectsProps<J extends CatalogJobOption> {
  categories: string[];
  submenus: string[];
  jobs: J[];
  category: string;
  submenu: string;
  job: J | null;
  onCategoryChange: (category: string) => void;
  onSubmenuChange: (submenu: string) => void;
  onJobChange: (job: J | null) => void;
  formatCategory: (key: string) => string;
}

const nonEmpty = (v: string | null | undefined): v is string => !!v && v.trim() !== '';

/** Only a real (non-empty) value is a selection; '' is the native-select desync. */
export function ignoreDesyncReset(apply: (value: string) => void) {
  return (value: string) => { if (nonEmpty(value)) apply(value); };
}

export function CatalogJobSelects<J extends CatalogJobOption>({
  categories, submenus, jobs, category, submenu, job,
  onCategoryChange, onSubmenuChange, onJobChange, formatCategory,
}: CatalogJobSelectsProps<J>) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="space-y-2">
        <Label>Category</Label>
        <Select value={category} onValueChange={ignoreDesyncReset(onCategoryChange)}>
          <SelectTrigger>
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {categories.filter(nonEmpty).map((cat) => (
              <SelectItem key={cat} value={cat}>
                {formatCategory(cat)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Submenu</Label>
        <Select
          value={submenu}
          onValueChange={ignoreDesyncReset(onSubmenuChange)}
          disabled={!category}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select submenu" />
          </SelectTrigger>
          <SelectContent>
            {submenus.filter(nonEmpty).map((sub) => (
              <SelectItem key={sub} value={sub}>
                {sub}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Job</Label>
        <Select
          value={job?.id || ''}
          onValueChange={ignoreDesyncReset((value) => onJobChange(jobs.find((j) => j.id === value) ?? null))}
          disabled={!submenu || jobs.length === 0}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select job" />
          </SelectTrigger>
          <SelectContent>
            {jobs.filter((j) => nonEmpty(j.id)).map((j) => (
              <SelectItem key={j.id} value={j.id}>
                {j.label} ({j.minutes_default} min)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export interface CatalogSearchJob {
  id: string;
  label: string;
  category_key: string;
  sub_menu: string;
}

/**
 * One search result → the complete selection, committed in one transition.
 * The picked row is the catalog row itself (id, minutes, skill …). The option
 * lists are seeded so they contain the selected values; lists that still
 * describe the same category / submenu are kept (extended if needed), lists
 * for a different one are replaced by the seed until the effects load them.
 */
export function seedCatalogJobSelection<J extends CatalogSearchJob>(
  picked: J,
  current: { category: string; submenu: string; submenus: string[]; jobs: J[] },
): { category: string; submenu: string; submenus: string[]; jobs: J[]; job: J } {
  const sameCategory = current.category === picked.category_key;
  const sameSubmenu = sameCategory && current.submenu === picked.sub_menu;
  const submenus = sameCategory
    ? (current.submenus.includes(picked.sub_menu) ? current.submenus : [...current.submenus, picked.sub_menu])
    : [picked.sub_menu];
  const listed = sameSubmenu ? current.jobs.find((j) => j.id === picked.id) : undefined;
  const jobs = sameSubmenu ? (listed ? current.jobs : [...current.jobs, picked]) : [picked];
  return { category: picked.category_key, submenu: picked.sub_menu, submenus, jobs, job: listed ?? picked };
}
